import React from 'react'

interface State {
  hasError:       boolean
  errorMessage:   string | null
  componentStack: string | null
}

export default class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { hasError: false, errorMessage: null, componentStack: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, errorMessage: error?.message ?? String(error) }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error)
    console.error('componentStack:', info.componentStack)
    this.setState({ componentStack: info.componentStack ?? null })
  }

  render() {
    if (this.state.hasError) {
      const { errorMessage, componentStack } = this.state
      return (
        <div className="flex flex-col items-center justify-center p-6 text-center min-h-[200px]">
          <p className="text-3xl mb-3">😕</p>
          <p className="font-semibold text-[#2C2A26] mb-1">Something went wrong</p>
          <p className="text-sm text-[#7A7468] mb-3">
            Try refreshing the page if this keeps happening.
          </p>

          {errorMessage && (
            <pre className="w-full max-w-md mb-3 whitespace-pre-wrap break-words rounded-md bg-[#F5F0E8] px-3 py-2 text-left text-xs text-[#7A3A1A]">
              {errorMessage}
            </pre>
          )}

          {componentStack && (
            <details className="w-full max-w-md mb-4 text-left">
              <summary className="text-xs text-[#7A7468] cursor-pointer">Component stack</summary>
              <pre className="mt-2 whitespace-pre-wrap break-words rounded-md bg-[#F5F0E8] px-3 py-2 text-[10px] leading-[1.4] text-[#7A7468]">
                {componentStack}
              </pre>
            </details>
          )}

          <button
            onClick={() => this.setState({ hasError: false, errorMessage: null, componentStack: null })}
            className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-[#6B9E78]"
          >
            Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
